import React, { useCallback, useEffect, useMemo } from 'react'
import { generatePath, useLocation, useParams } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { COUNTRY } from '@qflow/constants'

import * as routes from 'portal/routes'
import PropTypes from 'prop-types'

import { UpgradeToPro } from './UpgradeToPro'

import { useResponsive } from 'portal/lib/hooks'
import { SideBar, icons } from 'portal/lib/primitives'

import { projectsGetSaga } from 'portal/store/projects/actions'
import { projectGetDetailsSaga } from 'portal/store/projectDetails/actions'

import { BIReportSideBarButton } from './BIReportSideBarButton'

import { ProjectSettingsSidebarButton } from './ProjectSettingsSidebarButton'
import { AccountSettingsSidebarButton } from './AccountSettingsSidebarButton'
import { useMixpanelEvents } from './Mixpanel'

export function Sidebar() {
  const { accountId, projectId } = useParams()
  const location = useLocation()
  const dispatch = useDispatch()
  const { isMobile } = useResponsive()

  const lastUsedProjectId = sessionStorage.getItem('last-used-project-id')

  const { projects, isLoading } = useSelector(state => state.projects)
  const projectDetails = useSelector(state => state.projectDetails.modules)

  const enabledModules = useMemo(() => {
    let tempObj = {}
    if (projectDetails && projectDetails.moduleTags) {
      projectDetails.moduleTags.forEach(element => {
        tempObj[element.moduleTagDefinitionName.toLowerCase()] =
          element.isTagged
      })
    }
    return tempObj
  }, [projectDetails])

  const {
    trackDashboardEventClick,
    trackDashboardProEventClick,
    trackEventsPageEventClick
  } = useMixpanelEvents()

  // If current page has a projectId save in session storage
  //  in case we need to reference from a page that does not have this, e.g.Dashboard
  useEffect(() => {
    if (projectId && lastUsedProjectId !== projectId) {
      sessionStorage.setItem('last-used-project-id', projectId)
    }
  }, [lastUsedProjectId, projectId])

  useEffect(() => {
    if (!projects && !isLoading) {
      dispatch(projectsGetSaga(accountId))
    }
  }, [projects, isLoading, dispatch, accountId])

  // Check if we need to refresh project details
  const getRefreshedProjectDetails = useMemo(() => {
    return (
      !projectDetails ||
      (projectId && projectDetails.projectId !== projectId) ||
      (!projectId && projectDetails.projectId !== lastUsedProjectId)
    )
  }, [lastUsedProjectId, projectDetails, projectId])

  useEffect(() => {
    if (getRefreshedProjectDetails) {
      if (accountId && projectId) {
        dispatch(projectGetDetailsSaga(accountId, projectId))
      } else if (accountId && lastUsedProjectId) {
        dispatch(projectGetDetailsSaga(accountId, lastUsedProjectId))
      } else if (accountId && projects && projects.length > 0) {
        // Default to first project
        dispatch(projectGetDetailsSaga(accountId, projects[0].projectId))
      }
    }
  }, [
    accountId,
    dispatch,
    getRefreshedProjectDetails,
    lastUsedProjectId,
    projectId,
    projects
  ])

  const createUri = useCallback(
    (route, moduleName, tab) => {
      if (projects && projects.length > 0 && !isLoading) {
        return generatePath(route, {
          accountId,
          projectId: projectId
            ? projectId
            : lastUsedProjectId
              ? lastUsedProjectId
              : projects[0].projectId,
          moduleName,
          tab
        })
      }
      return ''
    },
    [accountId, isLoading, lastUsedProjectId, projectId, projects]
  )

  const createAccountSettingsUri = useCallback(
    route => {
      if (accountId) {
        return generatePath(route, {
          accountId
        })
      }
      return ''
    },
    [accountId]
  )

  const recordsUriDelivery = createUri(routes['DELIVERY'], 'delivery', 'table')
  const eventsUriDelivery = createUri(routes['DELIVERY'], 'delivery', 'events')

  const recordsUriWaste = createUri(routes['WASTE'], 'waste', 'table')
  const eventsUriWaste = createUri(routes['WASTE'], 'waste', 'events')

  const utilitiesReadings = createUri(routes.UTILITIES_READINGS, null, null)
  const utilitiesMeters = createUri(routes.UTILITIES_METERS, null, null)
  // const utilitiesEvents = createUri(routes.UTILITIES_EVENTS, null, null)

  const projectSettingsUri = createUri(routes.PROJECTSETTINGS, null, null)

  const accountSettingsUri = createAccountSettingsUri(routes.ACCOUNT_SETTINGS)

  const onDashboardClick = useCallback(() => {
    trackDashboardEventClick(projectId, accountId)
  }, [projectId, accountId, trackDashboardEventClick])

  const onDashboardProClick = useCallback(() => {
    trackDashboardProEventClick(projectId, accountId)
  }, [projectId, accountId, trackDashboardProEventClick])

  const onEventsPageClick = useCallback(() => {
    trackEventsPageEventClick(projectId, accountId)
  }, [projectId, accountId, trackEventsPageEventClick])

  const utilitiesEnabledAccounts = [
    '0569755e-cc43-4c44-9982-6bc8bbe34c70', // BW Construction
    '26532b6e-ac23-4142-b74d-c48d6cb33d7c', // Demo UK
    '7bad3c12-4d21-4530-92c7-2d507b071921', // Demo US
    'b789cf31-8376-4fda-9b8a-f29bc33e1041', // SPEN 132kV Projects
    '6cfe1952-c541-4b5a-bd8c-5e3cd2460b00', // SPEN Group
    '15f4e60c-f806-490a-87a6-0c2a276aa598', // SPEN Major Projects
    'd1dfbeb2-6168-4a96-b2e1-49d3c5de69f5', // SPEN SPD
    'e5230866-33b7-4d77-a5d5-df79366e8580', // SPEN SPM
    '219bcd2a-ca69-40cd-806c-364c28db4184', // SPEN TOps
    '0d515068-1281-4d79-bd50-bb5b4add902d' // Grosvenor
  ]

  const utilitiesFeatureFlag =
    window.location.hostname !== 'app.qualisflow.com' ||
    utilitiesEnabledAccounts.includes(accountId)

  return (
    <SideBar.Show>
      <SideBar.TopSection>
        {!isMobile && (
          <SideBar.SideBarGroup
            groupName="Dashboards"
            groupIcon={<icons.Dashboard />}
            active={location.pathname.includes('bireport')}
          >
            <BIReportSideBarButton
              reportName="Standard"
              reportType="Dashboard"
              onClick={onDashboardClick}
            />
            <BIReportSideBarButton
              reportName="Pro"
              reportType="Professional"
              onClick={onDashboardProClick}
            />
          </SideBar.SideBarGroup>
        )}
        {projectDetails && (
          <>
            <SideBar.SideBarGroup
              groupName="Deliveries"
              groupIcon={<icons.Modules.Delivery />}
              active={location.pathname.includes('delivery')}
              disabled={!enabledModules.delivery}
            >
              <SideBar.NavButton
                disabled={recordsUriDelivery == ''}
                id="Table"
                to={recordsUriDelivery}
              >
                Records
              </SideBar.NavButton>
              <SideBar.NavButton
                disabled={eventsUriDelivery == ''}
                id="Events"
                to={eventsUriDelivery}
                onClick={onEventsPageClick}
              >
                Events
              </SideBar.NavButton>
            </SideBar.SideBarGroup>

            {projectDetails.country !== COUNTRY.USA && (
              <SideBar.SideBarGroup
                groupName="Waste"
                groupIcon={<icons.Modules.Waste />}
                active={location.pathname.includes('waste')}
                disabled={!enabledModules.waste}
              >
                <SideBar.NavButton
                  disabled={recordsUriWaste == ''}
                  id="Table"
                  to={recordsUriWaste}
                >
                  Records
                </SideBar.NavButton>
                <SideBar.NavButton
                  disabled={eventsUriWaste == ''}
                  id="Events"
                  to={eventsUriWaste}
                  onClick={onEventsPageClick}
                >
                  Events
                </SideBar.NavButton>
              </SideBar.SideBarGroup>
            )}
            {utilitiesFeatureFlag && (
              <SideBar.SideBarGroup
                groupName="Utilities"
                groupIcon={<icons.Modules.Electricity />}
                active={location.pathname.includes('utilities')}
                disabled={!projectDetails.isProLicence}
                UpgradeToProComponent={UpgradeToPro}
              >
                <SideBar.NavButton
                  disabled={utilitiesReadings == ''}
                  to={utilitiesReadings}
                >
                  Readings
                </SideBar.NavButton>
                <SideBar.NavButton
                  disabled={utilitiesMeters == ''}
                  to={utilitiesMeters}
                >
                  Meters
                </SideBar.NavButton>
                {/* <SideBar.NavButton
                      disabled={utilitiesEvents == ''}
                      to={utilitiesEvents}
                    >
                      Events
                    </SideBar.NavButton> */}
              </SideBar.SideBarGroup>
            )}
          </>
        )}
      </SideBar.TopSection>

      <SideBar.BottomSection>
        <ProjectSettingsSidebarButton
          accountId={accountId}
          projectId={projectId}
          projectsTable={projectSettingsUri}
        />
        <AccountSettingsSidebarButton
          accountId={accountId}
          accountsTable={accountSettingsUri}
        />
      </SideBar.BottomSection>
    </SideBar.Show>
  )
}

SideBar.propTypes = {
  dateDisabled: PropTypes.bool
}
